import { useState } from 'react';
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IndicationType } from '@/types/opportunity';
import { INDICATIONS } from '@config/indications';
import { Button } from '@components/common/Button';

interface IndicationSelectorProps {
  onNext: (indications: IndicationType[]) => void;
  onBack?: () => void;
  initialValue?: IndicationType[];
  healthworkerColor?: string;
}

export function IndicationSelector({
  onNext,
  onBack,
  initialValue = [],
  healthworkerColor,
}: IndicationSelectorProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<IndicationType[]>(initialValue);

  const handleToggle = (type: IndicationType) => {
    setSelected((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type]
    );
  };

  const handleNext = () => {
    if (selected.length > 0) {
      onNext(selected);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('indications.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('indications.subtitle')}
      </Typography>

      <FormGroup>
        {INDICATIONS.map((indication) => (
          <Paper
            key={indication.type}
            sx={{
              mb: 1,
              p: 2,
              backgroundColor: selected.includes(indication.type)
                ? healthworkerColor ? `${healthworkerColor}20` : 'primary.light'
                : 'background.paper',
              borderLeft: selected.includes(indication.type) && healthworkerColor
                ? `4px solid ${healthworkerColor}`
                : undefined,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selected.includes(indication.type)}
                  onChange={() => handleToggle(indication.type)}
                  sx={{ minHeight: 48, minWidth: 48 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {t(`indications.${indication.type}.label`)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`indications.${indication.type}.description`)}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, width: '100%' }}
            />
          </Paper>
        ))}
      </FormGroup>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleNext}
        disabled={selected.length === 0}
        sx={{ mt: 3 }}
      >
        {t('indications.nextAction')}
      </Button>

      {onBack && (
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onBack}
          sx={{ mt: 1 }}
        >
          {t('indications.backToHCW')}
        </Button>
      )}
    </Box>
  );
}
